import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Droplet, Syringe as SyringeIcon, ArrowRight, Info } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

/**
 * Peptide reconstitution calculator.
 *
 * Formula (identical to the cellgenic reference tool):
 *   concentration_mg_per_mL = vial_strength_mg / water_volume_mL
 *   draw_volume_mL          = dose_mg / concentration_mg_per_mL
 *   insulin_units_U100      = draw_volume_mL * 100     // U-100 syringe
 *
 * All inputs are picked via preset chips or free-text override.  The
 * syringe visualisation shades the fill against a 1 mL barrel so the
 * researcher can eyeball the correct mark before drawing.
 */

const DOSE_PRESETS = [0.1, 0.25, 0.5, 1, 2, 2.5, 5, 7.5, 10, 12.5, 15];         // mg
const STRENGTH_PRESETS = [1, 5, 10, 15, 20, 50];                                // mg
const WATER_PRESETS = [0.5, 1, 1.5, 2, 2.5, 3];                                 // mL

function fmt(n, digits = 2) {
  if (!Number.isFinite(n)) return "—";
  return Number(n.toFixed(digits)).toString();
}

export default function Calculator() {
  const [dose, setDose] = useState(0.5);          // mg
  const [strength, setStrength] = useState(10);   // mg per vial
  const [water, setWater] = useState(2);          // mL
  const [products, setProducts] = useState([]);

  // Pull the current catalog once — the researcher who lands on this page is
  // very likely to also add a vial + BAC water to their bag, so we surface
  // the whole in-stock catalog at the bottom.
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, []);

  const conc = useMemo(() => (water > 0 ? strength / water : 0), [strength, water]);
  const drawMl = useMemo(() => (conc > 0 ? dose / conc : 0), [dose, conc]);
  const units = useMemo(() => drawMl * 100, [drawMl]);
  const overdraw = drawMl > 1; // exceeds a standard 1 mL insulin syringe

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="border-b border-[var(--line)] bg-[var(--paper-2)]">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="label-eyebrow"><FlaskConical className="inline h-3.5 w-3.5 mr-1" /> Laboratory tool</div>
          <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">
            Peptide <span className="gradient-text">calculator.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-[var(--ink-soft)]">
            Calculate reconstitution volumes, working concentration, and draw-to marks for
            standard U-100 insulin syringes. Enter your dose, vial strength, and bacteriostatic
            water volume — the result updates live.
          </p>
        </div>
      </section>

      {/* Calculator body */}
      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
        {/* Inputs */}
        <div className="space-y-8">
          <InputBlock
            icon={<SyringeIcon className="h-4 w-4" />}
            label="Dose of peptide"
            unit="mg per injection"
            presets={DOSE_PRESETS}
            value={dose}
            onChange={setDose}
            testKey="dose"
          />
          <InputBlock
            icon={<FlaskConical className="h-4 w-4" />}
            label="Strength of peptide vial"
            unit="mg total per vial"
            presets={STRENGTH_PRESETS}
            value={strength}
            onChange={setStrength}
            testKey="strength"
          />
          <InputBlock
            icon={<Droplet className="h-4 w-4" />}
            label="Bacteriostatic water added"
            unit="mL diluent"
            presets={WATER_PRESETS}
            value={water}
            onChange={setWater}
            testKey="water"
          />
        </div>

        {/* Results */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="border border-[var(--line)] bg-[var(--paper-2)] rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--line)] flex items-center justify-between">
              <div className="label-eyebrow">Results</div>
              {overdraw && (
                <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--crimson)] flex items-center gap-1">
                  <Info className="h-3 w-3" /> Exceeds 1 mL syringe
                </div>
              )}
            </div>

            <ResultRow
              label="Peptide dose"
              value={<span data-testid="result-dose">{fmt(dose, 3)} mg</span>}
              hint={`= ${fmt(dose * 1000, 0)} mcg`}
            />
            <ResultRow
              label="Draw syringe to"
              value={
                <span className="text-[var(--terra)] glow-cyan text-2xl" data-testid="result-draw">
                  {fmt(drawMl, 3)} mL
                </span>
              }
              hint={`≈ ${fmt(units, 0)} units on a U-100 insulin syringe`}
              emphasized
            />
            <Syringe fillFraction={Math.min(1, drawMl)} unitsLabel={fmt(units, 0)} />
            <ResultRow
              label="Vial contains"
              value={<span data-testid="result-vial">{fmt(strength, 2)} mg</span>}
              hint={`in ${fmt(water, 2)} mL diluent`}
            />
            <ResultRow
              label="Concentration"
              value={<span data-testid="result-conc">{fmt(conc, 3)} mg/mL</span>}
              hint={`= ${fmt(conc * 1000, 0)} mcg/mL`}
              last
            />
          </div>

          <div className="mt-4 text-[11px] font-mono text-[var(--ink-muted)] leading-relaxed">
            Values calculated from the equation
            <code className="mx-1 px-1.5 py-0.5 bg-[var(--paper-2)] rounded text-[var(--ink)]">draw = dose ÷ (strength ÷ water)</code>
            Laboratory reference tool. Not medical advice. Compounds sold strictly for research use.
          </div>
        </div>
      </section>

      {/* Method note */}
      <section className="border-t border-[var(--line)] bg-[var(--paper-2)]">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="label-eyebrow">How the math works</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
            <Step n="01" title="Choose your dose" body="The amount of peptide per administration, in mg. Convert from mcg by dividing by 1,000." />
            <Step n="02" title="Set vial strength" body="Total mg of lyophilized peptide in the vial before reconstitution. Read this from the COA." />
            <Step n="03" title="Add BAC water" body="Volume of bacteriostatic water used to reconstitute the vial, in mL. Concentration = strength ÷ water." />
            <Step n="04" title="Read the syringe" body="The draw volume tells you exactly how far to pull a U-100 syringe. Units = mL × 100." />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/shop?cat=research-peptides" className="btn-primary inline-flex items-center gap-2" data-testid="calc-shop-peptides">
              Shop reference peptides <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/shop?cat=laboratory-supplies" className="btn-outline text-sm inline-flex items-center gap-2" data-testid="calc-shop-bacwater">
              Shop bacteriostatic water
            </Link>
            <Link to="/coa" className="btn-outline text-sm inline-flex items-center gap-2" data-testid="calc-verify-coa">
              Verify a batch
            </Link>
          </div>
        </div>
      </section>

      {/* Product catalog — surface every in-stock reference compound so the
          researcher can add a vial straight from the calculator flow. */}
      {products.length > 0 && (
        <section className="border-t border-[var(--line)] bg-[var(--paper-2)]" data-testid="calc-products-section">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-14 md:py-16">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="label-eyebrow"><FlaskConical className="inline h-3.5 w-3.5 mr-1" /> Reference-grade catalog</div>
                <h2 className="h-display text-3xl md:text-5xl mt-2 leading-[1.04]">
                  Ready-to-<span className="gradient-text">reconstitute.</span>
                </h2>
                <p className="mt-3 max-w-2xl text-sm text-[var(--ink-soft)]">
                  Batch-verified compounds and lab supplies. Every vial ships with its own COA
                  and a batch sticker you can scan to open the calculator with the right strength
                  pre-filled.
                </p>
              </div>
              <Link
                to="/shop"
                className="btn-outline text-sm inline-flex items-center gap-2 self-start md:self-end"
                data-testid="calc-view-all"
              >
                View all products <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="calc-products-grid">
              {products.map((p) => <ProductCard key={p.sku} p={p} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */

function InputBlock({ icon, label, unit, presets, value, onChange, testKey }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-7 w-7 rounded-md bg-[var(--terra)]/10 text-[var(--terra)]">{icon}</span>
          <div>
            <div className="font-semibold text-[15px]">{label}</div>
            <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[var(--ink-muted)]">{unit}</div>
          </div>
        </div>
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-28 px-3 py-2 border border-[var(--line)] bg-[var(--paper-3)] text-[var(--ink)] font-mono text-sm rounded-md focus:border-[var(--terra)] outline-none"
          data-testid={`calc-${testKey}-input`}
        />
      </div>
      <div className="flex flex-wrap gap-2" data-testid={`calc-${testKey}-presets`}>
        {presets.map((p) => {
          const active = Math.abs(p - value) < 1e-6;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={`px-3.5 py-1.5 rounded-full border font-mono text-xs transition-colors ${
                active
                  ? "border-[var(--terra)] bg-[var(--terra)]/15 text-[var(--terra)] glow-cyan"
                  : "border-[var(--line)] bg-[var(--paper-2)] text-[var(--ink-soft)] hover:border-[var(--terra)]/60 hover:text-[var(--ink)]"
              }`}
              data-testid={`calc-${testKey}-preset-${p}`}
            >
              {p}{testKey === "water" ? "mL" : "mg"}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultRow({ label, value, hint, emphasized = false, last = false }) {
  return (
    <div className={`px-6 py-4 flex items-baseline justify-between gap-4 ${last ? "" : "border-b border-[var(--line)]"} ${emphasized ? "bg-[var(--paper-3)]/40" : ""}`}>
      <div className="min-w-0">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--ink-muted)]">{label}</div>
        {hint && <div className="text-[11px] font-mono text-[var(--ink-muted)] mt-0.5">{hint}</div>}
      </div>
      <div className="font-display text-lg tracking-tight text-[var(--ink)] whitespace-nowrap">{value}</div>
    </div>
  );
}

function Syringe({ fillFraction, unitsLabel }) {
  // Barrel spans from x=44 to x=244 (200 units wide, 1 mL = 100 units on U-100 scale).
  const width = 200;
  const fillW = Math.min(width, Math.max(0, fillFraction * width));
  return (
    <div className="px-6 py-5 border-b border-[var(--line)]">
      <div className="label-eyebrow mb-2">Insulin syringe · U-100 · 1 mL</div>
      <svg viewBox="0 0 320 70" className="w-full h-auto" data-testid="calc-syringe">
        {/* Plunger tab */}
        <rect x="4" y="26" width="16" height="18" rx="2" fill="var(--paper-3)" stroke="var(--line)" />
        <line x1="20" y1="35" x2="44" y2="35" stroke="var(--line)" strokeWidth="2" />
        {/* Barrel */}
        <rect x="44" y="18" width={width} height="34" rx="3" fill="var(--paper-3)" stroke="var(--line)" />
        {/* Filled portion */}
        {fillW > 0 && (
          <rect x="44" y="18" width={fillW} height="34" rx="3" fill="var(--terra)" fillOpacity="0.55" />
        )}
        {/* Tick marks — every 10 units */}
        {Array.from({ length: 11 }).map((_, i) => (
          <g key={i}>
            <line x1={44 + i * 20} y1={i % 5 === 0 ? 12 : 15} x2={44 + i * 20} y2="18" stroke="var(--ink-muted)" strokeWidth={i % 5 === 0 ? 1.5 : 1} />
            {i % 5 === 0 && (
              <text x={44 + i * 20} y="10" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="var(--ink-muted)">
                {i * 10}
              </text>
            )}
          </g>
        ))}
        {/* Neck + needle */}
        <path d="M244 26 L262 26 L280 32 L280 38 L262 44 L244 44 Z" fill="var(--paper-3)" stroke="var(--line)" />
        <line x1="280" y1="35" x2="316" y2="35" stroke="var(--ink-soft)" strokeWidth="2" />
      </svg>
      <div className="text-[11px] font-mono text-[var(--ink-muted)] mt-2 text-center">
        Draw to <span className="text-[var(--terra)] font-semibold">{unitsLabel}</span> on the barrel
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="border border-[var(--line)] bg-[var(--paper)] rounded-md p-4">
      <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--terra)]">{n}</div>
      <div className="font-semibold mt-1.5 text-[15px]">{title}</div>
      <p className="mt-2 text-[13px] text-[var(--ink-soft)] leading-relaxed">{body}</p>
    </div>
  );
}
